/* eslint-disable no-restricted-syntax */
import '../ModernPropertyTable.scss';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';

import { getBaseScenario } from 'redux/PlannerModeSlice';
import { STATUS } from 'projectConstants';
import DeleteGroupModal from 'components/DeleteGroupModal';

import SelectionActionBar from '../components/SelectionActionBar';

import useBuilderPropertyMetadataSource from './useBuilderPropertyMetadataSource';
import usePropertyTableRowsFromMetadata from './usePropertyTableRowsFromMetadata';
import usePropertyTableColumnsLayout from './usePropertyTableColumnsLayout';
import usePropertyTableInteractionController from './usePropertyTableInteractionController';
import PropertyTableLoadingState from './PropertyTableLoadingState';
import PropertyTableReactGrid from './PropertyTableReactGrid';
import { applySavingOverlayToPropertyTableRows } from './buildPropertyTableGridRows';

const BuilderModePropertyTableMetadataView = ({
  planPermission,
  useV1Checkbox = false,
  hideTableAddRow = false,
}: {
  planPermission?: boolean;
  useV1Checkbox?: boolean;
  hideTableAddRow?: boolean;
}) => {
  const { blockId } = useParams();
  const baseScenario = useSelector(getBaseScenario);
  const scenarioId = baseScenario?.id;

  const { blockMetadata, blockMetadataStatus, metadataIndicators, refetch } =
    useBuilderPropertyMetadataSource(blockId, scenarioId);

  const { tableData, rows: baseRows } = usePropertyTableRowsFromMetadata(
    blockMetadata,
    metadataIndicators,
    blockId,
    scenarioId,
    { includeFooterRow: !hideTableAddRow },
  );

  const { setHostRef, columns, onColumnResized } = usePropertyTableColumnsLayout();

  const planPermissionReadOnly = Boolean(planPermission);

  const {
    gridProperties,
    deleteModal,
    setDeleteModal,
    customCellTemplates,
    savingPropertyCell,
    handleFocusLocationChanged,
    handleCellChange,
    handleRowsReorder,
    handleContextMenu,
    handleLocationChanged,
    handleDeleteConfirm,
  } = usePropertyTableInteractionController({
    blockId,
    refetchMetadata: refetch,
    tableData,
    rows: baseRows,
    columns,
    planPermissionReadOnly,
    useV1Checkbox,
  });

  const savingOverlayRows = useMemo(
    () => applySavingOverlayToPropertyTableRows(baseRows, savingPropertyCell, true) ?? baseRows,
    [baseRows, savingPropertyCell],
  );

  // Checkbox selection for SelectionActionBar — checked state is overlaid
  // here rather than threaded through the incrementally-rebuilt row pipeline
  // (same approach as ModernDataTable/PropertyTableOpz).
  const [checkedRowIds, setCheckedRowIds] = useState<Array<string | number>>([]);
  const selectableRowIds = useMemo(
    () =>
      (savingOverlayRows || [])
        .filter((row: any) => row?.rowId !== 'header' && row?.rowId !== 'footer')
        .map((row: any) => row.rowId),
    [savingOverlayRows],
  );
  const rows = useMemo(() => {
    if (!savingOverlayRows) return savingOverlayRows;
    const selectedSet = new Set(checkedRowIds);
    return savingOverlayRows.map((row: any) => {
      if (row?.cells?.[0]?.type !== 'checkbox') return row;
      if (row.rowId === 'header') {
        const total = selectableRowIds.length;
        const selectedCount = selectableRowIds.filter((id: any) => selectedSet.has(id)).length;
        return {
          ...row,
          cells: [
            {
              ...row.cells[0],
              checked: total > 0 && selectedCount === total,
              isIndeterminate: selectedCount > 0 && selectedCount < total,
            },
            ...row.cells.slice(1),
          ],
        };
      }
      return {
        ...row,
        cells: [{ ...row.cells[0], checked: selectedSet.has(row.rowId) }, ...row.cells.slice(1)],
      };
    });
  }, [savingOverlayRows, checkedRowIds, selectableRowIds]);

  // The row-selection checkbox is purely local UI state, so it's handled
  // here instead of in the interaction controller (which only knows about
  // cells that persist to the API).
  const onCellsChanged = useCallback(
    (changes: any) => {
      const change = changes?.[0];
      if (change?.columnId === 'checkbox') {
        const checked = !!change?.newCell?.checked;
        if (change?.rowId === 'header') {
          setCheckedRowIds(checked ? [...selectableRowIds] : []);
        } else {
          setCheckedRowIds((previous) =>
            checked ? [...previous, change.rowId] : previous.filter((id) => id !== change.rowId),
          );
        }
        return;
      }
      handleCellChange(changes);
    },
    [handleCellChange, selectableRowIds],
  );

  const showLoading =
    blockMetadataStatus === STATUS.LOADING && (!blockMetadata || metadataIndicators.length === 0);

  const assignHostReference = useCallback(
    (node: HTMLDivElement | null) => {
      setHostRef(node);
    },
    [setHostRef],
  );

  return (
    <Box
      className='builder-mode-revamp-data-table'
      ref={assignHostReference}
      height={'100%'}
      width={'100%'}
      display={'flex'}
      flexDirection={'column'}
      flex={1}
      overflow={'hidden'}
    >
      {showLoading ? <PropertyTableLoadingState /> : null}
      {!showLoading ? (
        <SelectionActionBar
          selectedIds={checkedRowIds}
          onClearSelection={() => setCheckedRowIds([])}
        />
      ) : null}
      {!showLoading &&
      !!columns?.length &&
      !!rows?.length &&
      // Same guard as the other two grids — never hand ReactGrid a
      // transient columns/cells count mismatch.
      rows.every((row: any) => row?.cells?.length === columns.length) ? (
        <PropertyTableReactGrid
          rows={rows}
          columns={columns}
          gridProperties={gridProperties}
          customCellTemplates={customCellTemplates}
          onRowsReordered={handleRowsReorder}
          onFocusLocationChanging={handleLocationChanged}
          onFocusLocationChanged={handleFocusLocationChanged}
          onCellsChanged={onCellsChanged}
          onContextMenu={planPermission ? undefined : handleContextMenu}
          onColumnResized={planPermission ? undefined : onColumnResized}
          stickyBottomRows={hideTableAddRow ? 0 : undefined}
        />
      ) : null}
      <DeleteGroupModal
        isOpen={deleteModal.status}
        onClose={() => setDeleteModal({ indicatorId: '', indicatorName: '', status: false })}
        itemName={deleteModal.indicatorName || 'some name'}
        onDelete={handleDeleteConfirm}
        orgChart={true}
      />
    </Box>
  );
};

export default BuilderModePropertyTableMetadataView;
